import React, { useContext, useEffect, useState } from "react";
import usePosOrders from "../../../services/Pos/usePosOrders";
import useProfileDashboard from "../../../services/Settings/useProfileDashboard";
import { toast, ToastContainer } from "react-toastify";
import PrintSummaryReport from "./PrintSummaryReport";
import PrintSummaryTotals from "./PrintSummaryTotals";
import { GlobalContext } from "../../../Context/GlobalContext";
import LoadingScreen from "../../../utils/LoadingScreen";
import NoIndex from "../../../components/NoIndex";
import { RefreshCcw, X } from "lucide-react";
import { exportAnyJsonToExcel } from "../../../utils/exportAnyJsonToExcel";
import { formatDateTime } from "../../../utils/dateFormatter";

const getOrderTypeDisplay = (order) => {
  const type = String(order.orderType || "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "_");

  if (type === "dining" || type === "dinein") {
    const table = order.table_name || order.tableNumber || order.table_no;

    if (table) {
      return `DINING / ${table}`;
    }

    return "DINING";
  }

  if (type === "room_order" || type === "roomorder") {
    const room = order.table_name || order.tableNumber || order.table_no;

    if (room) {
      return `ROOM ORDER / ${room}`;
    }

    return "ROOM ORDER";
  }

  if (type === "home_delivery" || type === "homedelivery") {
    return "HOME DELIVERY";
  }

  if (type === "takeaway" || type === "takeway") {
    return "TAKEAWAY";
  }

  return order.orderType?.toUpperCase() || "N/A";
};

const normalizeMode = (mode) => {
  if (!mode) return "";

  const m = mode.toLowerCase().replace(/\s/g, "");

  //  CARD FIRST (IMPORTANT FIX)
  if (
    m.includes("creditcard") ||
    m.includes("debitcard") ||
    m.includes("card")
  ) {
    return "card";
  }

  //  UPI AFTER
  if (
    m.includes("upi") ||
    m.includes("gpay") ||
    m.includes("googlepay") ||
    m.includes("phonepe") ||
    m.includes("bhim") ||
    m.includes("paytm") ||
    m.includes("amazonpay") ||
    m.includes("cred")
  ) {
    return "upi";
  }

  if (m.includes("cash")) return "cash";
  if (m.includes("unpaid")) return "unpaid";

  return m;
};

const getTypeStyle = (type) => {
  const value = String(type || "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "_");

  switch (value) {
    case "homedelivery":
    case "home_delivery":
    case "home_delivery_order":
      return "bg-blue-100 text-blue-700 border border-blue-200";

    case "takeway":
    case "takeaway":
    case "take_away":
      return "bg-green-100 text-green-700 border border-green-200";

    case "dining":
    case "dinein":
    case "dine_in":
      return "bg-purple-100 text-purple-700 border border-purple-200";

    case "room_order":
    case "roomorder":
      return "bg-orange-100 text-orange-700 border border-orange-200";

    default:
      return "bg-gray-100 text-gray-600 border border-gray-200";
  }
};

const getModeStyle = (mode) => {
  const normalized = normalizeMode(mode);

  switch (normalized) {
    case "cash":
      return "bg-emerald-50 text-emerald-700 border border-emerald-200";

    case "upi":
      return "bg-blue-50 text-blue-700 border border-blue-200";

    case "card":
      return "bg-amber-50 text-amber-700 border border-amber-200";

    case "unpaid":
      return "bg-red-50 text-red-600 border border-red-200";

    default:
      return "bg-gray-50 text-gray-600 border border-gray-200";
  }
};

const formatStatusLabel = (status) => {
  if (!status || String(status).trim() === "") return "N/A";

  return String(status)
    .replaceAll("_", " ")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const getStatusStyle = (status) => {
  const value = String(status || "")
    .toLowerCase()
    .trim();

  if (["completed", "closed", "delivered", "successful"].includes(value)) {
    return "bg-green-100 text-green-700 border border-green-200";
  }

  if (["hold", "pending", "initiated", "received"].includes(value)) {
    return "bg-yellow-100 text-yellow-700 border border-yellow-200";
  }

  if (["paid", "accepted", "preparing"].includes(value)) {
    return "bg-blue-100 text-blue-700 border border-blue-200";
  }

  if (["cancelled", "canceled", "rejected", "failed"].includes(value)) {
    return "bg-red-100 text-red-700 border border-red-200";
  }

  if (
    [
      "dispatched",
      "assigned",
      "ready_for_takeaway",
      "ready_for_dinning",
    ].includes(value)
  ) {
    return "bg-purple-100 text-purple-700 border border-purple-200";
  }

  return "bg-gray-100 text-gray-700 border border-gray-200";
};

const getSafeMode = (mode) => {
  if (!mode || String(mode).trim() === "") {
    return "N/A";
  }
  return mode;
};

const hasEncodedEmojiPattern = (value = "") => {
  return /\[Emoji:[^\]]+\]/i.test(value);
};

const getSafeCustomerDisplayName = (name, mobile) => {
  const cleanName = String(name || "").trim();

  if (!cleanName) return mobile || "Unknown Customer";

  if (hasEncodedEmojiPattern(cleanName)) {
    return mobile || "Unknown Customer";
  }

  return cleanName;
};

const safeAmount = (value) => {
  const num = Number(value || 0);
  return Number.isFinite(num) ? Math.max(0, num) : 0;
};

const getGstType = (order, reciptDetails, userID) => {
  let storedTaxConfig = {};

  try {
    const raw = sessionStorage.getItem(`pos_org_tax_config_${userID}`);
    storedTaxConfig = raw ? JSON.parse(raw) : {};
  } catch (error) {
    console.error("Tax config parse error:", error);
    storedTaxConfig = {};
  }

  const value = String(
    storedTaxConfig?.gstType ||
      storedTaxConfig?.gst_type ||
      order?.gst_type ||
      order?.gstType ||
      order?.tax_type ||
      order?.taxType ||
      reciptDetails?.gstType ||
      reciptDetails?.gst_type ||
      "",
  )
    .toLowerCase()
    .trim();

  console.log("VIEW MODAL GST TYPE DEBUG:", {
    storedTaxConfig,
    orderGstType: order?.gst_type || order?.gstType,
    receiptGstType: reciptDetails?.gst_type || reciptDetails?.gstType,
    finalValue: value,
  });

  if (
    value === "incl" ||
    value === "inclusive" ||
    value === "included"
  ) {
    return "incl";
  }

  if (
    value === "excl" ||
    value === "exclusive" ||
    value === "excluded"
  ) {
    return "excl";
  }

  // Default fallback: inclusive, because inclusive GST should not add tax again.
  return "incl";
};

const getViewOrderTotal = (order, gstType) => {
  const subtotal = safeAmount(order?.subtotal || order?.totalOrderPrice);
  const discount = safeAmount(order?.discount_amount);

  const delivery = safeAmount(
    order?.delivery_fee ||
      order?.deliveryAmount ||
      order?.delivery_amount ||
      order?.deliveryCharges ||
      order?.delivery_charge,
  );

  const cgst = safeAmount(order?.tax_cgst);
  const sgst = safeAmount(order?.tax_sgst);

  // Other charges removed from total calculation
  const baseAmount = subtotal - discount + delivery;

  if (gstType === "incl") {
    return baseAmount;
  }

  return baseAmount + cgst + sgst;
};

const getViewTaxAmounts = (order, gstType, reciptDetails) => {
  const subtotal = safeAmount(order?.subtotal || order?.totalOrderPrice);
  const discount = safeAmount(order?.discount_amount);

  const taxableAmount = Math.max(subtotal - discount, 0);

  const receiptCgstRate = safeAmount(
    reciptDetails?.taxCgst ||
      reciptDetails?.tax_cgst ||
      reciptDetails?.cgstRate,
  );

  const receiptSgstRate = safeAmount(
    reciptDetails?.taxSgst ||
      reciptDetails?.tax_sgst ||
      reciptDetails?.sgstRate,
  );

  const cgstRate = receiptCgstRate || 2.5;
  const sgstRate = receiptSgstRate || 2.5;
  const totalRate = cgstRate + sgstRate;

  if (gstType === "incl") {
    const totalIncludedTax =
      totalRate > 0
        ? taxableAmount - taxableAmount / (1 + totalRate / 100)
        : 0;

    return {
      cgst: totalIncludedTax / 2,
      sgst: totalIncludedTax / 2,
    };
  }

  return {
    cgst: safeAmount(order?.tax_cgst),
    sgst: safeAmount(order?.tax_sgst),
  };
};


export default function PosOrders() {
  const {
    orders,
    summaryData,
    summaryLoading,
    getSummary,
    loading,
    getOrders,
    getOrdersV2,
    getOrderDetails,
    orderDetails,
  } = usePosOrders();

  const { fetchReceiptDetails } = useProfileDashboard();
  const { userID } = useContext(GlobalContext);
  const today = new Date().toISOString().split("T")[0];

  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [showModal, setShowModal] = useState(false);
  const [search, setSearch] = useState("");
  const [orderTypeFilter, setOrderTypeFilter] = useState("");
  const [paymentModeFilter, setPaymentModeFilter] = useState("");
  const [reciptDetails, setReciptDetails] = useState(null);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [summaryDate, setSummaryDate] = useState(today);
  const [summaryType, setSummaryType] = useState("");
  const [summaryActionLoading, setSummaryActionLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [summaryDownloadLoading, setSummaryDownloadLoading] = useState(false);

  const loadReceiptDetails = async () => {
    try {
      const data = await fetchReceiptDetails();

      const details = Array.isArray(data?.response)
        ? data.response[0]
        : data?.response || data || null;

      setReciptDetails(details);
      return details;
    } catch (error) {
      console.error("Receipt details error:", error);
      setReciptDetails(null);
      return null;
    }
  };

  useEffect(() => {
    getOrdersV2({ userID, fromDate, toDate });
    loadReceiptDetails();
  }, []);

  //  Search Click
  const handleApply = () => {
    getOrdersV2({ userID, fromDate, toDate });

    loadReceiptDetails();
  };

  const normalizeType = (type) => {
    if (!type) return "";

    const t = type.toLowerCase().replace(/\s/g, "");

    if (t.includes("home")) return "homedelivery";
    if (t.includes("take")) return "takeaway";
    if (t.includes("dine")) return "dining";

    return t;
  };

const filteredOrders = orders.filter((order) => {
  const searchText = search.toLowerCase();

  const matchesSearch =
    (order.orderid || "").toLowerCase().includes(searchText) ||
    (order.customer_name || "").toLowerCase().includes(searchText) ||
    (order.customer_mobile || "").toLowerCase().includes(searchText);

  const matchesType =
    !orderTypeFilter || normalizeType(order.orderType) === orderTypeFilter;

  const matchesMode =
    !paymentModeFilter ||
    normalizeMode(order.payment_mode) === paymentModeFilter;

  return matchesSearch && matchesType && matchesMode;
});

const latestFilteredOrders = [...filteredOrders].sort((a, b) => {
  const dateA = new Date(
    a.order_date || a.created_at || a.createdAt || a.orderDate || 0
  );

  const dateB = new Date(
    b.order_date || b.created_at || b.createdAt || b.orderDate || 0
  );

  return dateB - dateA;
});

  const handleRefresh = () => {
    getOrdersV2({ userID, fromDate, toDate });

    loadReceiptDetails();
  };

  const handleDownloadOrdersExcel = () => {
    if (!Array.isArray(filteredOrders) || filteredOrders.length === 0) {
      toast.info("No orders available to download");
      return;
    }

   const excelData = latestFilteredOrders.map((order) => ({
      "ODR ID": order.daily_order_id || "HOLD",
      "Bill No": order.orderid || "",
      Date: formatDateTime(order.order_date, true),
      Customer: getSafeCustomerDisplayName(
        order.customer_name,
        order.customer_mobile,
      ),
      Mobile: order.customer_mobile || "",
      "Order Type": getOrderTypeDisplay(order),
      "Order Amount": order.subtotal || "0",
      Discount: order.discount_amount || "0",
      CGST: Number(order.tax_cgst || 0).toFixed(2),
      SGST: Number(order.tax_sgst || 0).toFixed(2),
      "Net Amount": order.totalOrderPrice || "0",
      Mode: getSafeMode(order.payment_mode),
      Status: order.status || "",
      "Payment Status": order.payment_status || "",
    }));

    exportAnyJsonToExcel({
      data: excelData,
      filePrefix: `POS_Orders_${fromDate}_to_${toDate}`,
      sheetName: "POS Orders",
    });
  };

  const handleGenerateSummary = async (actionType = "print") => {
    const isExcel = actionType === "excel";

    if (summaryActionLoading || summaryDownloadLoading) return;

    try {
      if (isExcel) {
        setSummaryDownloadLoading(true);
      } else {
        setSummaryActionLoading(true);
      }

      const data = await getSummary({
        fromDate: summaryDate,
        toDate: summaryDate,
      });

      console.log("SUMMARY RESPONSE:", data);

      if (!data || data?.responseCode !== "00") {
        toast.info(`No summary data found for ${summaryDate}`);
        return;
      }

      const hasReportRows =
        Array.isArray(data?.response) && data.response.length > 0;

      const hasTotals =
        data?.data2 &&
        Object.keys(data.data2).length > 0 &&
        Number(data?.data2?.totalOrdersCount || 0) > 0;

      const latestReceiptDetails =
        reciptDetails || (await loadReceiptDetails());

      if (summaryType === "report") {
        if (!hasReportRows) {
          toast.info(`No report data found for ${summaryDate}`);
          return;
        }

        if (isExcel) {
          exportAnyJsonToExcel({
            data: data.response,
            filePrefix: `Summary_Report_${summaryDate}`,
            sheetName: "Summary Report",
          });
        } else {
          PrintSummaryReport(data, latestReceiptDetails);
        }
      }

      if (summaryType === "totals") {
        if (!hasTotals) {
          toast.info(`No summary totals found for ${summaryDate}`);
          return;
        }

        const totalsExcelData = [
          {
            Date: summaryDate,
            Total_Orders_Count: data?.data2?.totalOrdersCount || 0,
            Total_Cash_Collected_Amount:
              data?.data2?.totalCashCollectedAmount || 0,
            Total_UPI_Collected_Amount:
              data?.data2?.totalUPICollectedAmount || 0,
            Total_Card_Collected_Amount:
              data?.data2?.totalCardCollectedAmount || 0,
            Total_Summary_Amount:
              data?.data2?.totalSummaryAmount || data?.totalSummaryAmount || 0,
          },
        ];

        if (isExcel) {
          exportAnyJsonToExcel({
            data: totalsExcelData,
            filePrefix: `Summary_Totals_${summaryDate}`,
            sheetName: "Summary Totals",
          });
        } else {
          PrintSummaryTotals(data, latestReceiptDetails);
        }
      }

      setShowSummaryModal(false);
    } catch (error) {
      console.error("Summary generate error:", error);
      toast.error(
        isExcel
          ? "Unable to download summary excel"
          : "Unable to generate summary",
      );
    } finally {
      if (isExcel) {
        setSummaryDownloadLoading(false);
      } else {
        setSummaryActionLoading(false);
      }
    }
  };

  return (
    <>
      <NoIndex />
<ToastContainer/>
      <div className="p-4 min-h-0 h-full flex flex-col overflow-hidden montserrat-font-1">
        {/* Header */}
        <div className="bg-white p-5 rounded-lg mb-6 montserrat-font-1">
          <h2 className="text-xl font-semibold mb-4">POS Orders History</h2>

          {/* Filters */}
          <div className="flex gap-3 mb-3 items-end montserrat-font-1 justify-between whitespace-nowrap overflow-hidden">
            {/* LEFT SIDE */}
            <div className="flex gap-4 items-end">
              {/* From Date */}
              <div className="flex flex-col">
                <label className="text-xs text-gray-500 mb-1">From Date</label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="border rounded px-3 py-2 text-sm"
                />
              </div>

              {/* To Date */}
              <div className="flex flex-col">
                <label className="text-xs text-gray-500 mb-1">To Date</label>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="border rounded px-3 py-2 text-sm"
                />
              </div>

              {/* Buttons */}
              <div className="flex gap-2">
                <button
                  onClick={handleApply}
                  className="bg-green-500 hover:bg-green-600 text-white px-5 py-2 rounded text-sm"
                >
                  Apply
                </button>

                {/* Summary Report */}
                <button
                  className="bg-blue-500 hover:bg-blue-600 text-white px-5 py-2 rounded text-sm"
                  onClick={() => {
                    setSummaryDate(today);
                    setSummaryType("report");
                    setShowSummaryModal(true);
                  }}
                >
                  Summary Report
                </button>

                {/* Summary Totals */}
                <button
                  className="bg-green-500 hover:bg-green-600 text-white px-5 py-2 rounded text-sm"
                  onClick={() => {
                    setSummaryDate(today);
                    setSummaryType("totals");
                    setShowSummaryModal(true);
                  }}
                >
                  Summary Totals
                </button>

                <button
                  onClick={handleDownloadOrdersExcel}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2 rounded text-sm"
                >
                  Download Excel
                </button>

                <button
                  onClick={handleRefresh}
                  className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded text-sm shadow"
                >
                  <RefreshCcw className="w-4 h-4" />
                  Refresh
                </button>
              </div>
            </div>

            {/* RIGHT SIDE */}
            <div className="flex gap-4 items-end">
              {/* Order Type */}
              <div className="flex flex-col">
                <label className="text-xs text-gray-500 mb-1">Order Type</label>
                <select
                  value={orderTypeFilter}
                  onChange={(e) => setOrderTypeFilter(e.target.value)}
                  className="border rounded px-3 py-2 text-sm min-w-[140px]"
                >
                  <option value="">All Types</option>
                  <option value="homedelivery">Home Delivery</option>
                  <option value="takeaway">Takeaway</option>
                  <option value="dining">Dining</option>
                </select>
              </div>

              {/* Payment Mode */}
              <div className="flex flex-col">
                <label className="text-xs text-gray-500 mb-1">
                  Payment Mode
                </label>
                <select
                  value={paymentModeFilter}
                  onChange={(e) => setPaymentModeFilter(e.target.value)}
                  className="border rounded px-3 py-2 text-sm min-w-[140px]"
                >
                  <option value="">All Modes</option>
                  <option value="cash">Cash</option>
                  <option value="upi">UPI</option>
                  <option value="card">Card</option>
                  <option value="unpaid">Unpaid</option>
                </select>
              </div>
            </div>
          </div>
          <div className="flex flex-col">
            <label className="text-xs text-gray-500 mb-1">Search</label>
            <input
              type="text"
              placeholder="Order / Customer / Mobile"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="border rounded px-4 py-2 text-sm w-[260px] focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto border rounded-lg montserrat-font-1">
          <table className="min-w-full text-sm bg-white">
            <thead className="sticky top-0 z-10 bg-green-100 text-gray-700 font-semibold montserrat-font-1">
              <tr>
                <th className="px-3 py-2 text-left">ODR ID</th>
                <th className="px-3 py-2 text-left">BILL NO.</th>
                <th className="px-3 py-2 text-left">DATE</th>
                <th className="px-3 py-2 text-left">CUSTOMER</th>
                <th className="px-3 py-2 text-left">MOB</th>
                <th className="px-3 py-2 text-left">ODR TYPE</th>
                <th className="px-3 py-2 text-left">ODR AMT</th>
                <th className="px-3 py-2 text-left">DISCOUNT</th>
                {/* <th className="px-3 py-2 text-left">CGST</th>
                <th className="px-3 py-2 text-left">SGST</th> */}
                <th className="px-3 py-2 text-left">NET AMT</th>
                <th className="px-3 py-2 text-left">STATUS</th>
                <th className="px-3 py-2 text-left">MODE</th>
                <th className="px-3 py-2 text-left">DETAILS</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="13" className="py-10">
                    <div className="flex justify-center items-center">
                      <LoadingScreen text="Updating..." />
                    </div>
                  </td>
                </tr>
              ) : filteredOrders.length > 0 ? (
          latestFilteredOrders.map((order, index) => (
                  <tr
                    key={index}
                    className="border-t hover:bg-green-50 transition"
                  >
                    <td className="px-3 py-2 font-medium">
                      {order.daily_order_id || "HOLD"}
                    </td>

                    <td className="px-3 py-2">{order.orderid}</td>

                    <td className="px-3 py-2 text-gray-500">
  {formatDateTime(order.order_date, true)}
</td>

                    <td className="px-3 py-2">
                      {getSafeCustomerDisplayName(
                        order.customer_name,
                        order.customer_mobile,
                      )}
                    </td>

                    <td className="px-3 py-2">
                      {order.customer_mobile || "N/A"}
                    </td>

                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex items-center justify-center min-w-[120px] px-3 py-1 rounded-full text-xs font-semibold ${getTypeStyle(
                          order.orderType,
                        )}`}
                      >
                        {getOrderTypeDisplay(order)}
                      </span>
                    </td>

                    <td className="px-3 py-2">₹ {order.subtotal}</td>
                    <td className="px-3 py-2">₹ {order.discount_amount}</td>
                    {/* <td className="px-3 py-2">
                      ₹ {Number(order.tax_cgst).toFixed(2)}
                    </td>
                    <td className="px-3 py-2">
                      ₹ {Number(order.tax_sgst).toFixed(2)}
                    </td> */}

                    <td className="px-3 py-2 font-semibold">
                      ₹ {order.totalOrderPrice}
                    </td>

                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex items-center justify-center min-w-[90px] px-3 py-1 rounded-full text-xs font-semibold ${getStatusStyle(
                          order.status,
                        )}`}
                      >
                        {formatStatusLabel(order.status)}
                      </span>
                    </td>

                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex items-center justify-center min-w-[100px] px-3 py-1 rounded-full text-sm font-semibold ${getModeStyle(
                          order.payment_mode,
                        )}`}
                      >
                        {getSafeMode(order.payment_mode)}
                      </span>
                    </td>

                    <td className="px-3 py-2">
                      <button
                        disabled={detailsLoading}
                        onClick={async () => {
                          try {
                            setDetailsLoading(true);
                            setShowModal(true);

                            await getOrderDetails(order.orderid);
                          } catch (error) {
                            console.error("Order details error:", error);
                            toast.error("Unable to load order details");
                            setShowModal(false);
                          } finally {
                            setDetailsLoading(false);
                          }
                        }}
                        className={`border px-3 py-1 rounded ${
                          detailsLoading
                            ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                            : "hover:bg-gray-100"
                        }`}
                      >
                        {detailsLoading ? "Loading..." : "View"}
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="13" className="text-center py-5">
                    No orders found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* view button model */}
        {showModal && (
          <>
            {/* Overlay */}
            <div
              className="fixed inset-0 bg-black/50 z-40 montserrat-font-1"
              onClick={() => {
                if (!detailsLoading) {
                  setShowModal(false);
                }
              }}
            />

            {/* Modal */}
            <div className="fixed inset-0 flex items-center justify-center z-50 montserrat-font-1">
              <div className="bg-white w-[900px] max-h-[90vh] overflow-y-auto montserrat-font-1 rounded-xl shadow-lg p-6 relative">
                {/* Close */}
                <button
                  disabled={detailsLoading}
                  onClick={() => setShowModal(false)}
                  className={`absolute top-4 right-4 border px-4 py-2 rounded ${
                    detailsLoading
                      ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                      : "bg-gray-100 hover:bg-gray-200"
                  }`}
                >
                  CLOSE
                </button>

                {detailsLoading ? (
                  <div className="py-20 flex justify-center items-center">
                    <LoadingScreen text="Loading order details..." />
                  </div>
                ) : !orderDetails ? (
                  <div className="py-20 text-center text-gray-500">
                    No order details found
                  </div>
                ) : (
                  <>
                    {/* Header */}
                    <div className="text-center mb-4 montserrat-font-1">
                      <h2 className="text-2xl font-semibold">
                        Order Details #{orderDetails.orderid}
                      </h2>
                      <p className="text-gray-500">
  {formatDateTime(orderDetails.order_date, true)}
</p>
                      <hr className="mt-3" />
                    </div>

                    {/* Delivery Status */}
                    {orderDetails.orderType === "homedelivery" && (
                      <div className="mb-4">
                        <div className="bg-yellow-400 px-4 py-2 font-semibold flex justify-between">
                          <span>Delivery Status:</span>
                          <span>
                            {orderDetails.delivery_partner_status || "N"}
                          </span>
                        </div>

                        <div className="bg-yellow-100 px-4 py-2">
                          <strong>Delivery Person:</strong>{" "}
                          {orderDetails.delivery_person_name || "N/A"} |{" "}
                          {orderDetails.delivery_person_number || "N/A"}
                        </div>
                      </div>
                    )}

                    {/* Customer */}
                    <div className="mb-4 text-gray-700 montserrat-font-1">
                      <p>
                        <strong>Customer:</strong>{" "}
                        {getSafeCustomerDisplayName(
                          orderDetails.customer_name,
                          orderDetails.customer_mobile,
                        )}
                      </p>
                      <p>
                        <strong>Phone:</strong>{" "}
                        {orderDetails.customer_mobile || "N/A"}
                      </p>
                    </div>

                    {/* Items Table */}
                    <div className="overflow-x-auto mb-6 montserrat-font-1">
                      <table className="w-full border text-sm">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="border px-3 py-2">#</th>
                            <th className="border px-3 py-2 text-left">
                              Item Name
                            </th>
                            <th className="border px-3 py-2">Qty</th>
                            <th className="border px-3 py-2">Unit Price (₹)</th>
                            <th className="border px-3 py-2">Total (₹)</th>
                          </tr>
                        </thead>

                        <tbody>
                          {Array.isArray(orderDetails.items) &&
                          orderDetails.items.length > 0 ? (
                            orderDetails.items.map((item, i) => {
                              const price = Number(item.item_price || 0);
                              const qty = Number(item.quantity || 1);

                              return (
                                <tr key={i}>
                                  <td className="border px-3 py-2 text-center">
                                    {i + 1}
                                  </td>

                                  <td className="border px-3 py-2">
                                    {item.item_name || "N/A"}
                                    {item.sizeWords && ` (${item.sizeWords})`}
                                  </td>

                                  <td className="border px-3 py-2 text-center">
                                    {qty}
                                  </td>

                                  <td className="border px-3 py-2 text-center">
                                    ₹{price.toFixed(2)}
                                  </td>

                                  <td className="border px-3 py-2 text-center">
                                    ₹{(price * qty).toFixed(2)}
                                  </td>
                                </tr>
                              );
                            })
                          ) : (
                            <tr>
                              <td
                                colSpan="5"
                                className="border px-3 py-6 text-center text-gray-500"
                              >
                                No items found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* Summary */}
                    {(() => {
                     const gstType = getGstType(orderDetails, reciptDetails, userID);
const isInclusive = gstType === "incl";
const viewTotal = getViewOrderTotal(orderDetails, gstType);
const viewTax = getViewTaxAmounts(orderDetails, gstType, reciptDetails);

                      return (
                        <div className="space-y-2 text-gray-700 montserrat-font-1">
                          <div className="flex justify-between">
                            <span>Subtotal:</span>
                            <span>
                              ₹ {Number(orderDetails.subtotal || 0).toFixed(2)}
                            </span>
                          </div>

                          <div className="flex justify-between">
                            <span>Discount Amount:</span>
                            <span>
                              ₹{" "}
                              {Number(
                                orderDetails.discount_amount || 0,
                              ).toFixed(2)}
                            </span>
                          </div>

                       <div className="flex justify-between">
  <span>CGST {isInclusive ? "(Included)" : ""}:</span>
  <span>₹ {viewTax.cgst.toFixed(2)}</span>
</div>

                        <div className="flex justify-between">
  <span>SGST {isInclusive ? "(Included)" : ""}:</span>
  <span>₹ {viewTax.sgst.toFixed(2)}</span>
</div>

                          {Number(
                            orderDetails.delivery_fee ||
                              orderDetails.deliveryAmount ||
                              orderDetails.delivery_amount ||
                              orderDetails.deliveryCharges ||
                              0,
                          ) > 0 && (
                            <div className="flex justify-between">
                              <span>Delivery Charges:</span>
                              <span>
                                ₹{" "}
                                {Number(
                                  orderDetails.delivery_fee ||
                                    orderDetails.deliveryAmount ||
                                    orderDetails.delivery_amount ||
                                    orderDetails.deliveryCharges ||
                                    0,
                                ).toFixed(2)}
                              </span>
                            </div>
                          )}

                          <hr />

                          <div className="flex justify-between font-semibold text-lg">
                            <span>
                              Total{" "}
                              {isInclusive ? "(GST Included)" : "(GST Extra)"}:
                            </span>
                            <span>₹ {viewTotal.toFixed(2)}</span>
                          </div>
                        </div>
                      );
                    })()}
                    {/* Payment Status */}
                    <div className="text-right mt-6 font-semibold text-lg montserrat-font-1">
                      Payment Status:{" "}
                      {orderDetails.payment_status?.toUpperCase() || "N/A"}
                    </div>
                  </>
                )}
              </div>
            </div>
          </>
        )}

        {/* summary Reports model */}

        {showSummaryModal && (
          <>
            {/* Overlay */}
            <div
              className="fixed inset-0 bg-black/40 z-40 montserrat-font-1"
              onClick={() => {
                if (!summaryActionLoading && !summaryDownloadLoading) {
                  setShowSummaryModal(false);
                }
              }}
            />

            {/* Modal */}
            <div className="fixed inset-0 flex items-center justify-center z-50 montserrat-font-1">
              <div
                className="bg-white w-[430px] rounded-lg p-5 shadow-lg relative"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  disabled={summaryActionLoading || summaryDownloadLoading}
                  onClick={() => {
                    if (summaryActionLoading || summaryDownloadLoading) return;
                    setShowSummaryModal(false);
                  }}
                  className={`absolute top-3 right-3 rounded-full p-1.5 transition ${
                    summaryActionLoading || summaryDownloadLoading
                      ? "text-gray-300 cursor-not-allowed"
                      : "text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                  }`}
                  title="Close"
                >
                  <X size={18} />
                </button>

                <h3 className="text-lg font-semibold mb-4 text-center pr-8">
                  {summaryType === "totals"
                    ? "Summary Totals"
                    : "Summary Report"}
                </h3>

                <input
                  type="date"
                  value={summaryDate}
                  onChange={(e) => setSummaryDate(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  className="border w-full px-3 py-2 rounded mb-4 bg-white cursor-pointer"
                />

                <div className="flex justify-end gap-2 flex-wrap montserrat-font-1">
                  <button
                    disabled={summaryActionLoading || summaryDownloadLoading}
                    className={`text-white px-4 py-2 rounded ${
                      summaryActionLoading
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-blue-500 hover:bg-blue-600"
                    }`}
                    onClick={() => handleGenerateSummary("print")}
                  >
                    {summaryActionLoading ? "Printing..." : "Print"}
                  </button>

                  <button
                    disabled={summaryActionLoading || summaryDownloadLoading}
                    className={`text-white px-4 py-2 rounded ${
                      summaryDownloadLoading
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-emerald-600 hover:bg-emerald-700"
                    }`}
                    onClick={() => handleGenerateSummary("excel")}
                  >
                    {summaryDownloadLoading
                      ? "Downloading..."
                      : "Download Excel"}
                  </button>

                  <button
                    type="button"
                    disabled={summaryActionLoading || summaryDownloadLoading}
                    onClick={() => {
                      if (summaryActionLoading || summaryDownloadLoading)
                        return;
                      setShowSummaryModal(false);
                    }}
                    className={`text-white px-4 py-2 rounded ${
                      summaryActionLoading || summaryDownloadLoading
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-gray-500 hover:bg-gray-600"
                    }`}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
